"use client";

import { useEffect, useState } from "react";
import { markPaidAction } from "@frontend/lib/actions";
import { ActionForm, SubmitButton } from "@frontend/components/action-form";
import { CopyValue } from "@frontend/components/copy-value";
import { Callout, Field, OptionalField, Pill } from "@frontend/components/ui";
import type { PaymentMethod } from "@creators/backend/lib/collabs/payment-methods";
import { formatINR } from "@creators/backend/lib/money";

export type PayMethodView = {
  key: PaymentMethod;
  label: string;
  preferred: boolean;
  upi?: { id: string; uri: string; qrDataUri: string | null } | null;
  bank?: { accountNumber: string; ifsc: string; accountName: string | null } | null;
  other?: string | null;
};

/**
 * Paying happens in the payer's own banking app, so this is a handoff, not a
 * checkout. It exists to make the handoff unambiguous: one amount, the methods
 * the creator actually accepts, and a QR that carries both so nothing is
 * retyped. The record form lives here too so the selected method can be posted
 * without asking again — and without passing a render function across the
 * server→client boundary.
 */
export function PayNowDialog({
  collabId,
  milestoneId,
  amountPaise,
  creatorName,
  triggerLabel,
  methods,
}: {
  collabId: string;
  milestoneId: string;
  amountPaise: number;
  creatorName: string;
  triggerLabel: string;
  methods: PayMethodView[];
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<PaymentMethod>(
    methods.find((m) => m.preferred)?.key ?? methods[0]?.key ?? "upi",
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const method = methods.find((m) => m.key === active) ?? methods[0] ?? null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2.5 w-full rounded-xl bg-money-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-money-700"
      >
        {triggerLabel}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="pay-now-title"
            onClick={(event) => event.stopPropagation()}
            className="max-h-[min(720px,92dvh)] w-full max-w-md overflow-y-auto rounded-2xl bg-surface shadow-xl"
          >
            <div className="flex items-start justify-between gap-3 border-b border-line/70 px-5 py-4">
              <div className="min-w-0">
                <h3 id="pay-now-title" className="font-semibold text-ink">
                  Pay {creatorName}
                </h3>
                <p className="mt-0.5 text-sm text-ink-muted">
                  <span className="tabular font-semibold text-ink">{formatINR(amountPaise)}</span>{" "}
                  due now
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded-lg px-2 py-1 text-sm text-ink-muted hover:bg-canvas hover:text-ink"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4">
              {methods.length === 0 ? (
                <Callout tone="amber">
                  {creatorName} has not added a payment method yet. Ask them to add one before you
                  send anything.
                </Callout>
              ) : (
                <>
                  {methods.length > 1 ? (
                    <div
                      role="tablist"
                      aria-label="Payment method"
                      className="flex gap-1.5 rounded-xl bg-canvas p-1"
                    >
                      {methods.map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          role="tab"
                          aria-selected={m.key === active}
                          onClick={() => setActive(m.key)}
                          className={
                            m.key === active
                              ? "flex-1 rounded-lg bg-surface px-3 py-1.5 text-sm font-medium text-ink shadow-sm"
                              : "flex-1 rounded-lg px-3 py-1.5 text-sm font-medium text-ink-muted hover:text-ink"
                          }
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {method ? (
                    <div className="mt-4">
                      {method.preferred && methods.length > 1 ? (
                        <div className="mb-3">
                          <Pill tone="green">{creatorName} prefers this</Pill>
                        </div>
                      ) : null}
                      <MethodBody method={method} amountPaise={amountPaise} />
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="border-t border-line/70 bg-canvas px-5 py-4">
              <p className="mb-3 text-xs leading-relaxed text-ink-subtle">
                Once the transfer leaves your bank, record it here. {creatorName} confirms when it
                lands — the platform never holds the money.
              </p>
              <MarkPaidForm
                collabId={collabId}
                milestoneId={milestoneId}
                method={active}
                disabled={methods.length === 0}
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function MarkPaidForm({
  collabId,
  milestoneId,
  method,
  disabled,
}: {
  collabId: string;
  milestoneId: string;
  method: PaymentMethod;
  disabled: boolean;
}) {
  return (
    <details className="group">
      <summary className="cursor-pointer list-none text-sm font-medium text-brand-700 hover:underline">
        I have sent this — record it
      </summary>
      <div className="mt-3">
        <ActionForm action={markPaidAction}>
          <input type="hidden" name="collabId" value={collabId} />
          <input type="hidden" name="milestoneId" value={milestoneId} />
          <input type="hidden" name="method" value={method} />

          <Field label="Screenshot of the transfer" hint="PNG, JPEG or WebP, up to 5 MB.">
            <input
              type="file"
              name="screenshot"
              accept="image/png,image/jpeg,image/webp"
              required
              className="field-input file:mr-3 file:rounded-lg file:border-0 file:bg-canvas file:px-3 file:py-1 file:text-sm file:font-medium file:text-ink-muted"
            />
          </Field>

          <OptionalField
            label="Note"
            addLabel="Add a note"
            hint="UTR or anything else the creator should know."
          >
            <textarea name="note" rows={2} className="field-input resize-y" />
          </OptionalField>

          <SubmitButton variant="money" full pendingLabel="Recording…" disabled={disabled}>
            Record payment
          </SubmitButton>
        </ActionForm>
      </div>
    </details>
  );
}

function MethodBody({ method, amountPaise }: { method: PayMethodView; amountPaise: number }) {
  if (method.upi) {
    return (
      <div>
        {method.upi.qrDataUri ? (
          <div className="flex flex-col items-center rounded-xl border border-line bg-surface px-4 py-4">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={method.upi.qrDataUri}
              alt={`UPI QR code for ${formatINR(amountPaise)}`}
              width={176}
              height={176}
              className="size-44 rounded-lg"
            />
            <p className="mt-3 text-center text-xs leading-relaxed text-ink-muted">
              Scan with GPay, PhonePe, Paytm or any UPI app. The amount is already filled in.
            </p>
            <a
              href={method.upi.uri}
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-line-strong bg-surface px-4 py-2 text-sm font-medium text-ink transition hover:bg-canvas sm:hidden"
            >
              Open a UPI app
            </a>
          </div>
        ) : null}

        <dl className="mt-3 space-y-1.5">
          <CopyValue label="UPI ID" value={method.upi.id} />
        </dl>
      </div>
    );
  }

  if (method.bank) {
    return (
      <div>
        <dl className="space-y-1.5">
          <CopyValue label="Account" value={method.bank.accountNumber} />
          <CopyValue label="IFSC" value={method.bank.ifsc} />
          {method.bank.accountName ? (
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <dt className="text-ink-muted">Name</dt>
              <dd className="font-medium text-ink">{method.bank.accountName}</dd>
            </div>
          ) : null}
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-ink-muted">Amount</dt>
            <dd className="tabular font-semibold text-ink">{formatINR(amountPaise)}</dd>
          </div>
        </dl>
        <p className="mt-3 text-xs leading-relaxed text-ink-subtle">
          A NEFT or IMPS transfer usually lands the same day. Add a UTR in the note if you want.
        </p>
      </div>
    );
  }

  return <p className="text-sm leading-relaxed text-ink">{method.other}</p>;
}
